/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Constants for Auth component
 *
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.30.2
 * @since 1.0.1
 */

import { IAuthState, IDeviceTypes, ISubscriberTypeEnum } from '../types/authTypes';

/*
 * -------------------------------------------
 *         LOGIN PROCESS CONSTANTS
 * -------------------------------------------
 */

export const LOGIN_PROCESS = '@@b2b_portal_ui/authorization/LOGIN_PROCESS';
export const LOGIN_PROCESS_FAILED = '@@b2b_portal_ui/authorization/LOGIN_PROCESS_FAILED';
export const LOGIN_PROCESS_SUCCESSFUL = '@@b2b_portal_ui/authorization/LOGIN_PROCESS_SUCCESSFUL';
export const LOGIN_PROCESS_ERROR = '@@b2b_portal_ui/authorization/LOGIN_PROCESS_ERROR';

/*
 * -------------------------------------------
 *         LOAD AUTH DATA CONSTANTS
 * -------------------------------------------
 */

export const LOAD_AUTH_DATA = '@@b2b_portal_ui/authorization/LOAD_AUTH_DATA';
export const LOAD_AUTH_DATA_SUCCESSFUL = '@@b2b_portal_ui/authorization/LOAD_AUTH_DATA_SUCCESSFUL';
export const LOAD_AUTH_DATA_FAILURE = '@@b2b_portal_ui/authorization/LOAD_AUTH_DATA_FAILURE';

/*
 * -------------------------------------------
 *         UNAUTHORIZED
 * -------------------------------------------
 */

export const AUTH_UNAUTHORIZED = '@@b2b_portal_ui/authorization/AUTH_UNAUTHORIZED';

/*
 * -------------------------------------------
 *        LOGOUT CONSTANTS
 * -------------------------------------------
 */

export const LOGOUT_START = '@@b2b_portal_ui/authorization/LOGOUT_START';
export const LOGOUT_START_SUCCESSFUL = '@@b2b_portal_ui/authorization/LOGOUT_START_SUCCESSFUL';
export const LOGOUT_START_FAILURE = '@@b2b_portal_ui/authorization/LOGOUT_START_FAILURE';

export const REDIRECT_TO_SOHO = '@@b2b_portal_ui/authorization/REDIRECT_TO_SOHO';
export const REDIRECT_TO_SOHO_FAILURE = '@@b2b_portal_ui/authorization/REDIRECT_TO_SOHO_FAILURE';

export const AUTH_SET_LOGGED_IN = '@@b2b_portal_ui/authorization/AUTH_SET_LOGGED_IN';
export const AUTH_SET_IS_FORBIDDEN = '@@b2b_portal_ui/authorization/AUTH_SET_IS_FORBIDDEN';

export const SubscriberTypeEnum: ISubscriberTypeEnum = {
    MOBILE: 'MOBILE',
    FIXED: 'FIXED',
};

export enum AuthMenuEnum {
    ACCOUNTS = 'accounts',
    CAMPAIGNS = 'campaigns',
    CASHBACK = 'cashback',
    INQUIRIES = 'inquiries',
    MAIN = 'main',
    ORDERS = 'orders',
    PRODUCTS = 'products',
    SUBSCRIBERS_MOBILE = 'subscribersMobile',
    COLLECTIVE_PRODUCT = 'collectiveProduct',
}

export enum EAccountType {
    CREDIT = 'CREDIT',
    EMPTY = 'EMPTY',
    PREPAID = 'PREPAID',
}

export const initialAuthState: IAuthState = {
    data: {
        authorities: [],
        bannerProvider: {
            bannerGroupId: 0,
            url: '',
        },
        cached: false,
        captchaShouldBeShown: false,
        features: [],
        menu: {
            [AuthMenuEnum.ACCOUNTS]: true,
            [AuthMenuEnum.CAMPAIGNS]: true,
            [AuthMenuEnum.CASHBACK]: true,
            [AuthMenuEnum.INQUIRIES]: true,
            [AuthMenuEnum.MAIN]: true,
            [AuthMenuEnum.ORDERS]: true,
            [AuthMenuEnum.PRODUCTS]: true,
            [AuthMenuEnum.SUBSCRIBERS_MOBILE]: true,
            [AuthMenuEnum.COLLECTIVE_PRODUCT]: true,
        },
        message: '',
        onboarding: {
            isDisabledByUser: false,
        },
        expenses: {
            urls: {
                preliminaryData: '',
            },
        },
        notifications: {
            lastUserActionInterval: 0,
            pollingInterval: 0,
            showNotifications: false,
            showNews: false,
        },
        systemSettings: {
            files: {
                maxFileNameLength: 80,
                maxFileSize: 10485760,
            },
            notifier: {
                lastUserActionInterval: 0,
                notifierInterval: 0,
            },
        },
        user: {
            accountId: 0,
            accountType: EAccountType.PREPAID,
            bisId: 0,
            helpFlag: true,
            isCoordinator: false,
            isDashboardEnabled: false,
            isSingleAccount: true,
            isSubscriber: false,
            level: {
                account: true,
                agreement: true,
                customer: true,
                holding: true,
                subdivision: true,
            },
            state: '',
            subscriberType: null,
            susrId: 0,
            username: '',
            userTypeId: 0,
            redirectAllowed: false,
            redirected: false,
        },
    },
    errorMessage: '',
    isCaptchaShouldBeShown: false,
    isForbidden: false,
    isLoading: false,
    isServiceAccessLoggedIn: false,
    isNewsServiceAccessLoggedIn: false,
    loggedIn: false,
};

export enum AuthorityRoleEnum {
    ROLE_ACCOUNT_COORDINATOR = 'ROLE_ACCOUNT_COORDINATOR',
    ROLE_AGREEMENT_COORDINATOR = 'ROLE_AGREEMENT_COORDINATOR',
    ROLE_CUSTOMER_COORDINATOR = 'ROLE_CUSTOMER_COORDINATOR',
    ROLE_HOLDING_COORDINATOR = 'ROLE_HOLDING_COORDINATOR',
    ROLE_SUB_DIV_COORDINATOR = 'ROLE_SUB_DIV_COORDINATOR',
    ROLE_UNION_COORDINATOR = 'ROLE_UNION_COORDINATOR',
    ROLE_ACCOUNT_ASSISTANT = 'ROLE_ACCOUNT_ASSISTANT',
    ROLE_UNION_ASSISTANT = 'ROLE_UNION_ASSISTANT',
    ROLE_COORDINATOR_USER = 'ROLE_COORDINATOR_USER',
    ROLE_ASSISTANT_USER = 'ROLE_ASSISTANT_USER',
    ROLE_PRIVILEGED_USER = 'ROLE_PRIVILEGED_USER',
    ROLE_SUBSCRIBER_MANAGE_RATEPLAN = 'ROLE_SUBSCRIBER_MANAGE_RATEPLAN',
    ROLE_SUBSCRIBER_MANAGE = 'ROLE_SUBSCRIBER_MANAGE',
    ROLE_SUBSCRIBER_READ_ONLY = 'ROLE_SUBSCRIBER_READ_ONLY',
    ROLE_USER = 'ROLE_USER',
    ROLE_SERVICE_USER = 'ROLE_SERVICE_USER',
    ROLE_NEWS_SERVICE_USER = 'ROLE_NEWS_SERVICE_USER',
    ROLE_PASSWORD_CHANGE_REQUIRED = 'ROLE_PASSWORD_CHANGE_REQUIRED',
    ROLE_CUSTOM_USER = 'ROLE_CUSTOM_USER',
    ROLE_PROMISEDPAYMENTS_CONNECTION = 'ROLE_PROMISEDPAYMENTS_CONNECTION',
    ROLE_SUBSCRIBER_BLOCKING = 'ROLE_SUBSCRIBER_BLOCKING',
    ROLE_RATEPLAN_MANAGE = 'ROLE_RATEPLAN_MANAGE',
    ROLE_SERVICES_MANAGE = 'ROLE_SERVICES_MANAGE',
    ROLE_LIMITS_MANAGE = 'ROLE_LIMITS_MANAGE',
    ROLE_ACCESS_MANAGE = 'ROLE_ACCESS_MANAGE',
    ROLE_CHECK_MANAGE = 'ROLE_CHECK_MANAGE',
    ROLE_SIM_CHANGE = 'ROLE_SIM_CHANGE',
    ROLE_TAGS_IMPORT = 'ROLE_TAGS_IMPORT',
    ROLE_CALLS_FORWARDING = 'ROLE_CALLS_FORWARDING',
    ROLE_INQUIRIES_MANAGE = 'ROLE_INQUIRIES_MANAGE',
    ROLE_COLLECTIVE_MANAGE = 'ROLE_COLLECTIVE_MANAGE',
    ROLE_CALLS_DETAILING = 'ROLE_CALLS_DETAILING',
    ROLE_DOCS_MANAGE = 'ROLE_DOCS_MANAGE',
    ROLE_PERSONAL_MANAGE = 'ROLE_PERSONAL_MANAGE',
    ROLE_COST_SHARING = 'ROLE_COST_SHARING',
    ROLE_CASHBACK_MANAGE = 'ROLE_CASHBACK_MANAGE',
    ROLE_VASSERVICES_CREATE = 'ROLE_VASSERVICES_CREATE',
    ROLE_CASH_TRANSFER = 'ROLE_CASH_TRANSFER',
}

export enum EUserType {
    ID_NEWS_SERVICE_USER = -1, // Новостной сервисный пользователь
    SACS_ID_READONLY = 150190004, // АКП - чтение
    SACS_ID_MANAGE = 150190005, // АКП - управление
    SACS_ID_CHANGE_RATEPLAN = 150190006, // АКП - управление + смена ТП
    SACS_CAM_CUSTOMER_COORDINATOR = 150190102, // КК (координатор клиента)
    SACS_CAM_AGREEMENT_COORDINATOR = 150190101, // КД (координатор договора)
    SACS_CAM_ACCOUNT_COORDINATOR = 150190100, // КЛС (координатор лицевого счета)
    SACS_CAM_SUB_DIV_COORDINATOR = 150190106, // КСП (координатор структурного подразделения)
    SACS_CAM_HOLDING_COORDINATOR = 150190108, // КХ (координатор холдинга)
    SACS_CAM_CUSTOMER_ASSISTANT = 150190105, // ПКК (помощник координатора клиента)
    SACS_CAM_AGREEMENT_ASSISTANT = 150190104, // ПКД (помощник координатора договора)
    SACS_CAM_ACCOUNT_ASSISTANT = 150190103, // ПКЛС (помощник координатора лицевого счета)
    SACS_CAM_SUB_DIV_ASSISTANT = 150190107, // ПКСП (помощник коорд структурного подразделения)
    SACS_CAM_HOLDING_ASSISTANT = 150190109, // ПКХ (помощник координатора холдинга)
}

export const userTypesSubscribers: EUserType[] = [
    EUserType.SACS_ID_READONLY,
    EUserType.SACS_ID_MANAGE,
    EUserType.SACS_ID_CHANGE_RATEPLAN,
];

export const userTypesAssistant: EUserType[] = [
    EUserType.SACS_CAM_ACCOUNT_ASSISTANT,
    EUserType.SACS_CAM_AGREEMENT_ASSISTANT,
    EUserType.SACS_CAM_CUSTOMER_ASSISTANT,
    EUserType.SACS_CAM_HOLDING_ASSISTANT,
    EUserType.SACS_CAM_SUB_DIV_ASSISTANT,
];

export enum EServiceUserLoginResponseData {
    SERVICE_USER = 'user.auth.success',
    NEWS_SERVICE_USER = 'news.user.auth.success',
}

export const deviceTypes: IDeviceTypes = {
    browser: 'desktop',
    mobile: 'mobile',
    tablet: 'tablet',
    unknown: 'unknown',
};
